<template>
  <div class="reschedule-confirm-page">
    <Card class="order-card">
      <template #title>
        <span class="card-title-bar">订单信息</span>
      </template>
      <div class="train-line">
        <span class="emph">{{ pending?.trainInfo?.departureDate }}</span>
        <span class="emph">（{{ weekText }}）</span>
        <span class="emph">{{ pending?.trainInfo?.trainNumber }}</span>
        <span>次</span>
        <span class="emph">{{ pending?.trainInfo?.departure }}</span>
        <span>站</span>
        <span class="emph">（{{ pending?.trainInfo?.departureTime }}开）—</span>
        <span class="emph">{{ pending?.trainInfo?.arrival }}</span>
        <span>站</span>
        <span class="emph">（{{ pending?.trainInfo?.arrivalTime }}到）</span>
      </div>
      <Table
        :columns="columns"
        :data-source="passengersWithFee"
        :pagination="false"
        size="small"
        bordered
      />

      <Table
        v-if="passengerTypeFeeSummary.length"
        class="type-fee-summary-table"
        :columns="typeFeeSummaryColumns"
        :data-source="passengerTypeFeeSummary"
        :pagination="false"
        size="small"
        bordered
      />

      <Spin :spinning="rescheduleFeeLoading">
        <RefundRescheduleFeePanel
          v-if="rescheduleFeeAvailable"
          class="fee-panel-wrap"
          mode="reschedule"
          inline
          title="改签费用"
          :ticket-price-yuan="originalTicketPriceYuan"
          :reschedule-fee-yuan="Number(rescheduleFeeData.rescheduleFeeYuan)"
          :reschedule-service-fee-yuan="Number(rescheduleFeeData.serviceFeeYuan)"
          :price-diff-yuan="Number(rescheduleFeeData.priceDiffYuan)"
          :total-pay-yuan="Number(rescheduleFeeData.totalPayYuan)"
          :type-fee-breakdown="passengerTypeFeeBreakdown"
          @show-rules="rulesModalVisible = true"
        />
        <div
          v-else-if="!rescheduleFeeLoading && rescheduleFeeTried"
          class="fee-unavailable"
        >
          {{ RESCHEDULE_FEE_UNAVAILABLE_MSG }}
        </div>
      </Spin>

      <div class="action-row">
        <Button class="btn-cancel" @click="openCancelModal">取消改签</Button>
        <Button
          v-if="rescheduleFeeAvailable"
          type="primary"
          class="btn-submit"
          :loading="submitting"
          @click="handleImmediateReschedule"
        >
          立即改签
        </Button>
      </div>
    </Card>

    <Card title="温馨提示" class="tips-card">
      <ol class="tips-list">
        <li>
          席位已锁定，请点击「立即改签」完成改签。完成改签前，将无法购买其他车票，请尽快完成改签。
        </li>
        <li>改签待支付订单，在待支付订单中显示。</li>
        <li>
          改签时，若新票价高于原票价，需先支付新票全款，原票票款将按原支付方式退回；若新票价低于原票价，退还差额；若票价相同，无需支付。
        </li>
      </ol>
    </Card>

    <Modal
      :visible="cancelModalVisible"
      title="交易提示"
      :footer="null"
      width="520px"
      wrap-class-name="reschedule-cancel-modal"
      @cancel="cancelModalVisible = false"
    >
      <div class="cancel-modal-body">
        <QuestionCircleFilled class="cancel-icon" />
        <div class="cancel-text">
          <p class="cancel-question">您确认取消改签吗？</p>
          <p class="cancel-warning">
            一天内<span class="warn-num">3次</span>申请车票成功后取消改签，当天
            <span class="warn-highlight">将不能在网站购票</span>。
          </p>
        </div>
      </div>
      <div class="cancel-modal-actions">
        <Button @click="cancelModalVisible = false">取消</Button>
        <Button type="primary" class="btn-confirm-cancel" @click="confirmCancelReschedule">
          确认
        </Button>
      </div>
    </Modal>

    <RefundRescheduleRulesModal v-model:visible="rulesModalVisible" />

    <TrainWalletPayModal
      v-model:open="walletPayOpen"
      pay-type="reschedule"
      modal-title="改签支付"
      pay-subject="火车票改签"
      :amount="walletPayAmount"
      :order-sn="walletPayOrderSn"
      :out-order-sn="pending?.apiParams?.orderSn"
      :route-hint="walletPayRouteHint"
      :allow-zero-amount="true"
      @success="onWalletPaySuccess"
    />
  </div>
</template>

<script setup>
import {
  Card,
  Table,
  Button,
  Modal,
  Spin,
  message
} from 'ant-design-vue'
import { QuestionCircleFilled } from '@ant-design/icons-vue'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { getWeekNumber } from '@/utils'
import {
  TICKET_TYPE_LIST,
  ID_CARD_TYPE,
  SEAT_CLASS_TYPE_LIST
} from '@/constants'
import { fetchRescheduleTicket, fetchRescheduleFeeEstimate } from '@/service'
import {
  loadReschedulePending,
  clearReschedulePending,
  RESCHEDULE_PENDING_KEY
} from '@/utils/rescheduleStorage'
import { buildRescheduleFeeSnapshot } from '@/utils/feeSnapshot'
import RefundRescheduleFeePanel from '@/components/refund-reschedule/RefundRescheduleFeePanel.vue'
import RefundRescheduleRulesModal from '@/components/refund-reschedule/RefundRescheduleRulesModal.vue'
import TrainWalletPayModal from '@/components/pay/TrainWalletPayModal.vue'

const router = useRouter()
const RESCHEDULE_FEE_UNAVAILABLE_MSG = '暂无法获取费用，请联系客服'

const pending = ref(null)
const submitting = ref(false)
const cancelModalVisible = ref(false)
const rulesModalVisible = ref(false)
const walletPayOpen = ref(false)
const walletPayOrderSn = ref('')
const rescheduleFeeLoading = ref(false)
const rescheduleFeeTried = ref(false)
const rescheduleFeeData = ref(null)

const rescheduleFeeAvailable = computed(
  () => rescheduleFeeData.value?.available === true
)

const originalTicketPriceYuan = computed(() => {
  const passengers = pending.value?.passengers ?? []
  if (!passengers.length) return 0
  const totalCents = passengers.reduce((sum, p) => {
    if (p.originalAmount != null) return sum + p.originalAmount
    const avg = pending.value?.originalOrder?.amountPerPassengerYuan
    return sum + (avg != null ? Math.round(avg * 100) : 0)
  }, 0)
  return totalCents / 100
})

const findPassengerFeeItem = (passenger) => {
  const feePassengers = rescheduleFeeData.value?.passengers || []
  return feePassengers.find(
    (item) =>
      String(item.passengerId) === String(passenger.id) ||
      item.realName === passenger.realName
  )
}

const passengersWithFee = computed(() => {
  const list = pending.value?.passengers ?? []
  return list.map((passenger) => {
    const fee = findPassengerFeeItem(passenger)
    const newPriceYuan =
      passenger.amount != null ? passenger.amount / 100 : null
    let priceDiffYuan = fee?.priceDiffYuan != null ? Number(fee.priceDiffYuan) : null
    let rescheduleFeeYuan =
      fee?.rescheduleFeeYuan != null ? Number(fee.rescheduleFeeYuan) : null
    const serviceFeeYuan =
      fee?.serviceFeeYuan != null ? Number(fee.serviceFeeYuan) : null
    if (
      priceDiffYuan == null &&
      newPriceYuan != null &&
      passenger.originalAmount != null
    ) {
      priceDiffYuan = newPriceYuan - passenger.originalAmount / 100
    }
    if (rescheduleFeeYuan == null && fee?.payAmountYuan != null && priceDiffYuan != null) {
      rescheduleFeeYuan =
        Number(fee.payAmountYuan) - priceDiffYuan - (serviceFeeYuan ?? 0)
    }
    const payAmountYuan =
      fee?.payAmountYuan != null
        ? Number(fee.payAmountYuan)
        : rescheduleFeeYuan != null && priceDiffYuan != null
          ? rescheduleFeeYuan + (serviceFeeYuan ?? 0) + priceDiffYuan
          : null
    return {
      ...passenger,
      rescheduleFeeYuan,
      serviceFeeYuan,
      priceDiffYuan,
      payAmountYuan
    }
  })
})

const getPassengerTypeLabel = (discountType) =>
  TICKET_TYPE_LIST.find((i) => i.value === discountType)?.label ?? '成人票'

const sumPassengerOriginalYuan = (passenger) => {
  if (passenger?.originalAmount != null) {
    return passenger.originalAmount / 100
  }
  const avg = pending.value?.originalOrder?.amountPerPassengerYuan
  return avg != null ? Number(avg) : 0
}

const passengerTypeFeeSummary = computed(() => {
  const groups = new Map()
  for (const passenger of passengersWithFee.value) {
    const typeKey = passenger.discountType ?? 0
    if (groups.has(typeKey)) continue
    groups.set(typeKey, {
      key: `type-${typeKey}`,
      discountType: typeKey,
      passengerType: getPassengerTypeLabel(typeKey),
      originalPriceYuan: sumPassengerOriginalYuan(passenger),
      newPriceYuan: passenger.amount != null ? passenger.amount / 100 : null,
      rescheduleFeeYuan: passenger.rescheduleFeeYuan,
      serviceFeeYuan: passenger.serviceFeeYuan,
      priceDiffYuan: passenger.priceDiffYuan,
      payAmountYuan: passenger.payAmountYuan
    })
  }
  return Array.from(groups.values()).sort(
    (a, b) => (a.discountType ?? 0) - (b.discountType ?? 0)
  )
})

const formatBreakdownUnit = (value, digits = 2) => {
  if (value == null || Number.isNaN(Number(value))) return '—'
  const num = Math.abs(Number(value))
  return num.toFixed(digits).replace(/\.?0+$/, '') || '0'
}

const shortPassengerTypeName = (discountType) =>
  getPassengerTypeLabel(discountType).replace(/票$/, '')

const buildTypeFeeBreakdownLine = (getter, digits = 2) => {
  const groups = new Map()
  for (const passenger of passengersWithFee.value) {
    const typeKey = passenger.discountType ?? 0
    if (!groups.has(typeKey)) {
      groups.set(typeKey, {
        discountType: typeKey,
        name: shortPassengerTypeName(typeKey),
        unit: getter(passenger),
        count: 0
      })
    }
    groups.get(typeKey).count += 1
  }
  return Array.from(groups.values())
    .sort((a, b) => (a.discountType ?? 0) - (b.discountType ?? 0))
    .map((group) => `${group.name}：${formatBreakdownUnit(group.unit, digits)}X${group.count}`)
    .join('\n')
}

const passengerTypeFeeBreakdown = computed(() => ({
  ticketPrice: buildTypeFeeBreakdownLine(
    (p) => sumPassengerOriginalYuan(p),
    1
  ),
  rescheduleFee: buildTypeFeeBreakdownLine((p) => p.rescheduleFeeYuan ?? 0),
  rescheduleServiceFee: buildTypeFeeBreakdownLine((p) => p.serviceFeeYuan ?? 0),
  priceDiff: buildTypeFeeBreakdownLine((p) =>
    p.priceDiffYuan != null ? Math.abs(Number(p.priceDiffYuan)) : null
  ),
  totalPay: buildTypeFeeBreakdownLine((p) =>
    p.payAmountYuan != null ? Math.abs(Number(p.payAmountYuan)) : null
  )
}))

const formatTypeFeeCell = (value, digits = 2) => {
  if (rescheduleFeeLoading.value || !rescheduleFeeData.value?.available) return '—'
  if (value == null || Number.isNaN(Number(value))) return '—'
  const num = Number(value)
  return (num < 0 ? Math.abs(num) : num).toFixed(digits)
}

/** 高改低：票款差额为负，无需分销支付 */
const isHighToLowReschedule = computed(() => {
  const diff = rescheduleFeeData.value?.priceDiffYuan
  if (diff == null || !rescheduleFeeData.value?.available) return false
  return Number(diff) < 0
})

const columns = computed(() => [
  { title: '序号', dataIndex: 'keyNumber', width: 60 },
  { title: '姓名', dataIndex: 'realName' },
  {
    title: '证件类型',
    dataIndex: 'idType',
    customRender: ({ text }) =>
      ID_CARD_TYPE.find((i) => i.value === text)?.label ?? '-'
  },
  { title: '证件号码', dataIndex: 'idCard' },
  {
    title: '乘客类型',
    dataIndex: 'discountType',
    customRender: ({ text }) =>
      TICKET_TYPE_LIST.find((i) => i.value === text)?.label ?? '成人票'
  },
  {
    title: '席别',
    dataIndex: 'seatType',
    customRender: ({ text }) =>
      SEAT_CLASS_TYPE_LIST.find((i) => i.code === text)?.label ?? '-'
  },
  { title: '车厢', dataIndex: 'carriageNumber', customRender: () => '—' },
  { title: '席位号', dataIndex: 'seatNumber', customRender: () => '待分配' }
])

const typeFeeSummaryColumns = computed(() => [
  {
    title: '乘客类型',
    dataIndex: 'passengerType',
    width: 100
  },
  {
    title: '原票价',
    dataIndex: 'originalPriceYuan',
    customRender: ({ record }) => formatTypeFeeCell(record.originalPriceYuan, 1)
  },
  {
    title: '新票价',
    dataIndex: 'newPriceYuan',
    customRender: ({ record }) => formatTypeFeeCell(record.newPriceYuan, 1)
  },
  {
    title: '改签手续费',
    dataIndex: 'rescheduleFeeYuan',
    customRender: ({ record }) => formatTypeFeeCell(record.rescheduleFeeYuan)
  },
  {
    title: '改签服务费',
    dataIndex: 'serviceFeeYuan',
    customRender: ({ record }) => formatTypeFeeCell(record.serviceFeeYuan)
  },
  {
    title: '票款差额',
    dataIndex: 'priceDiffYuan',
    customRender: ({ record }) => formatTypeFeeCell(record.priceDiffYuan)
  },
  {
    title: isHighToLowReschedule.value ? '应退' : '应补',
    dataIndex: 'payAmountYuan',
    customRender: ({ record }) => formatTypeFeeCell(record.payAmountYuan)
  }
])

const weekText = computed(() => {
  const d = pending.value?.trainInfo?.departureDate
  return d ? getWeekNumber(dayjs(d).day()) : ''
})

/** 低改高或平改：需先走分销钱包支付（含金额为0的平改验密） */
const requiresRescheduleWalletPay = computed(() => !isHighToLowReschedule.value)

const walletPayAmount = computed(() => {
  if (!rescheduleFeeData.value?.available) return 0
  const total = Number(rescheduleFeeData.value.totalPayYuan ?? 0)
  return total < 0 ? 0 : total
})

const walletPayRouteHint = computed(() => {
  const t = pending.value?.trainInfo
  if (!t) return ''
  return `${t.departureDate} ${t.trainNumber}次 ${t.departure}-${t.arrival}`
})

const WALLET_ORDER_SN_MAX_LEN = 20

/** 分销钱包流水 order_number 最长 20，不可用「原单-CHG-时间戳」 */
const buildWalletPayOrderSn = () => {
  const millis = Date.now() % 1e12
  const seq = String(Math.floor(Math.random() * 10000)).padStart(4, '0')
  return `R${millis}${seq}`.slice(0, WALLET_ORDER_SN_MAX_LEN)
}

const ensureWalletPayOrderSn = (existing) => {
  if (
    existing &&
    existing.length <= WALLET_ORDER_SN_MAX_LEN &&
    !existing.includes('-CHG-')
  ) {
    return existing
  }
  return buildWalletPayOrderSn()
}

const submitRescheduleRequest = () => {
  if (!pending.value?.apiParams) {
    message.error('改签信息已失效，请重新办理')
    router.replace('/ticketList')
    return Promise.resolve()
  }
  submitting.value = true
  const originOrderSn = pending.value.apiParams?.orderSn
  const apiParams = {
    ...pending.value.apiParams,
    feeSnapshot: buildRescheduleFeeSnapshot(
      rescheduleFeeData.value,
      pending.value?.passengers ?? []
    )
  }
  return fetchRescheduleTicket(apiParams)
    .then((res) => {
      if (res.success) {
        clearReschedulePending()
        router.replace({
          path: '/rescheduleSuccess',
          query: {
            orderSn: res.data?.orderSn,
            originOrderSn: originOrderSn || ''
          }
        })
      } else {
        message.error(res.message || '改签失败')
      }
    })
    .catch(() => message.error('改签请求失败'))
    .finally(() => {
      submitting.value = false
    })
}

const onWalletPaySuccess = () => {
  submitRescheduleRequest()
}

const loadRescheduleFeeEstimate = () => {
  const params = pending.value?.apiParams
  if (!params) return
  rescheduleFeeLoading.value = true
  rescheduleFeeTried.value = false
  rescheduleFeeData.value = null
  fetchRescheduleFeeEstimate(params)
    .then((res) => {
      rescheduleFeeData.value = res?.data ?? null
    })
    .catch(() => {
      rescheduleFeeData.value = {
        available: false,
        message: RESCHEDULE_FEE_UNAVAILABLE_MSG
      }
    })
    .finally(() => {
      rescheduleFeeLoading.value = false
      rescheduleFeeTried.value = true
    })
}

const openCancelModal = () => {
  cancelModalVisible.value = true
}

const confirmCancelReschedule = () => {
  cancelModalVisible.value = false
  clearReschedulePending()
  message.info('已取消改签')
  router.replace('/ticketList')
}

const handleImmediateReschedule = () => {
  if (!pending.value?.apiParams) {
    message.error('改签信息已失效，请重新办理')
    router.replace('/ticketList')
    return
  }
  if (requiresRescheduleWalletPay.value) {
    if (!rescheduleFeeData.value?.available) {
      message.warning('费用信息加载中，请稍候再试')
      return
    }
    walletPayOrderSn.value = ensureWalletPayOrderSn(pending.value.walletPayOrderSn)
    pending.value.walletPayOrderSn = walletPayOrderSn.value
    sessionStorage.setItem(RESCHEDULE_PENDING_KEY, JSON.stringify(pending.value))
    walletPayOpen.value = true
    return
  }
  submitRescheduleRequest()
}

onMounted(() => {
  const data = loadReschedulePending()
  if (!data?.apiParams) {
    message.warning('未找到待确认的改签信息')
    router.replace('/ticketList')
    return
  }
  pending.value = data
  if (!data.walletPayOrderSn || data.walletPayOrderSn.length > WALLET_ORDER_SN_MAX_LEN
      || data.walletPayOrderSn.includes('-CHG-')) {
    pending.value.walletPayOrderSn = buildWalletPayOrderSn()
  }
  walletPayOrderSn.value = pending.value.walletPayOrderSn || ''
  loadRescheduleFeeEstimate()
})
</script>

<style lang="scss" scoped>
.reschedule-confirm-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 12px 0 24px;
}

.order-card {
  margin-bottom: 16px;
}

.card-title-bar {
  display: inline-block;
  background: #2563eb;
  color: #fff;
  padding: 4px 12px;
  font-weight: 600;
}

.train-line {
  margin-bottom: 12px;
  font-size: 14px;
  .emph {
    font-weight: 700;
    margin: 0 2px;
  }
}

.fee-panel-wrap {
  margin-top: 16px;
}

.type-fee-summary-table {
  margin-top: 12px;
}

.fee-unavailable {
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 4px;
  background: #f7f8fa;
  color: #64748b;
  font-size: 14px;
  text-align: center;
}

.action-row {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 20px;
}

.btn-cancel {
  min-width: 120px;
  height: 36px;
}

.btn-submit {
  min-width: 120px;
  height: 36px;
  background: #ff8001 !important;
  border-color: #ff8001 !important;
}

.tips-card {
  background: #fffdf4;
  border-color: #ffe08a;
}

.tips-list {
  margin: 0;
  padding-left: 20px;
  color: #475569;
  font-size: 13px;
  line-height: 1.8;
}

.cancel-modal-body {
  display: flex;
  gap: 16px;
  padding: 8px 0 16px;
}

.cancel-icon {
  font-size: 42px;
  color: #faad14;
  flex-shrink: 0;
}

.cancel-question {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px;
}

.cancel-warning {
  margin: 0;
  font-size: 14px;
  line-height: 1.8;
  color: #334155;
}

.warn-num {
  color: #f97316;
  font-weight: 700;
}

.warn-highlight {
  color: #f97316;
  font-weight: 700;
}

.cancel-modal-actions {
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-bottom: 8px;
}

.btn-confirm-cancel {
  background: #ff8001 !important;
  border-color: #ff8001 !important;
}
</style>
